import { Building2, RefreshCw } from 'lucide-react';
import { useBankAccounts } from '../hooks/useBankAccounts.js';
import { useEntityScope } from '../hooks/useEntityScope.js';
import { formatCurrency } from '../lib/format.js';
import { splitByPurpose } from '../lib/bankAccounts.js';
import { PROVIDER_LABELS } from '../lib/providers.js';
import EntityBadge from './EntityBadge.jsx';

export default function BalanceCard() {
  const { data, isLoading, isFetching, refetch } = useBankAccounts();
  const { isGroup } = useEntityScope();
  const accounts = Array.isArray(data) ? data : [];

  // Solde consolidé = trésorerie DISPONIBLE. Les sinking funds sont affichés
  // à part : l'argent est encore là, mais il est engagé sur une dette.
  const { operatingCents, sinkingCents, sinkingFunds } = splitByPurpose(accounts);

  return (
    <div className="rounded-xl bg-gradient-to-br from-slate-800 to-slate-700 text-white shadow-md px-5 py-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 size={16} className="opacity-70" aria-hidden />
          <span className="text-sm font-medium opacity-80">Solde consolidé</span>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="p-1.5 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50"
          aria-label="Actualiser"
        >
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      <div>
        <p className="text-xs opacity-60 mb-1">
          {sinkingFunds.length ? 'Disponible (hors provisions)' : 'Toutes banques'}
        </p>
        {isLoading ? (
          <div className="h-9 w-36 bg-white/10 animate-pulse rounded" />
        ) : (
          <p className="text-3xl font-bold tabular-nums">
            {accounts.length ? formatCurrency(operatingCents) : '—'}
          </p>
        )}
        {sinkingFunds.length > 0 && (
          <p className="text-xs opacity-60 mt-1 tabular-nums">
            + {formatCurrency(sinkingCents)} provisionnés sur {sinkingFunds.length} compte
            {sinkingFunds.length > 1 ? 's' : ''} dédié{sinkingFunds.length > 1 ? 's' : ''}
          </p>
        )}
      </div>

      {accounts.length > 0 && (
        <ul className="pt-2 border-t border-white/10 space-y-1 text-xs">
          {accounts.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-3">
              <span className="opacity-80 truncate flex items-center gap-1.5 min-w-0">
                {isGroup && a.entity_slug && (
                  <EntityBadge
                    slug={a.entity_slug}
                    name={a.entity_slug}
                    className="!bg-white/10 !text-white !ring-white/20"
                  />
                )}
                <span className="truncate">
                  {PROVIDER_LABELS[a.provider] ?? a.provider} · {a.name}
                </span>
                {a.purpose === 'sinking_fund' && (
                  <span className="shrink-0 text-[10px] px-1 py-0.5 rounded bg-white/10 opacity-80">
                    provision
                  </span>
                )}
              </span>
              <span className="tabular-nums font-medium shrink-0">
                {a.balance_cents != null ? formatCurrency(a.balance_cents) : '—'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
